import { useEffect } from 'react'

import { userAtom } from '../recoil/atoms'
import { authSelector } from '../recoil/selectors'
import { useRecoilValue, useSetRecoilState } from 'recoil'

import { GraphQlEnum } from '../utils/Enums'
import { useLazyQuery } from '@apollo/client'
import { GET_USER_WITH_EMAIL, GET_BILTY_OPS } from '../graphql/query'

import useMutate from './useMutate'
import { auth } from '../firebase/firebaseConfig'
import { mutateGraphQL } from '../services/MutateGraphQl'

import { useNavigate, useLocation } from 'react-router-dom'

export const useAuth = (setLoading = () => {}, setAuthorized, setAlert) => {
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const currLocation = pathname.split('/')?.[2]
	const setUserAtom = useSetRecoilState(userAtom)
	const userAuth = useRecoilValue(authSelector())
	const [fetchUserWithEmail] = useLazyQuery(GET_USER_WITH_EMAIL)
	const [fetchBiltyOps, { loading }] = useLazyQuery(GET_BILTY_OPS)
	const createBiltyOps = useMutate({ segment: 'BiltyOps', action: 'createBiltyOps' })

	useEffect(() => {
		if (!loading && userAuth) {
			if (userAuth?.user === 'no user') {
				setAlert({
					type: 'error',
					message: 'No user record for this email address.',
				})
				setLoading(false)
				localStorage.setItem('user', JSON.stringify(null))
			} else if (userAuth?.authorized === false) {
				setLoading(false)
				setAuthorized(false)
				setUserAtom(null)
				localStorage.setItem('user', JSON.stringify(null))
			} else if (userAuth?.authorized === true && currLocation === 'login') {
				setLoading(false)
				navigate('/biltyManagement/Import', { replace: true })
			}
		}
	}, [userAuth, loading]) // eslint-disable-line

	const signupUser = async (email, firstName, lastName) => {
		// GCP_DOWNTIME_MITIGATION: disable signup during GCP downtime
		return 'failure'
		/*		const response = await fetch(`https://asia-south1-${process.env.REACT_APP_PROJECT_ID}.cloudfunctions.net/createUser`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json;charset=utf-8' },
			body: JSON.stringify({ email: email }),
		}).then((res) => res.json())

		if (response?.uid) {
			await auth.signInWithEmailAndPassword(email, 'secretPassword')
			const token = await auth.currentUser.getIdToken()
			localStorage.setItem('token', JSON.stringify({ token: token }))
			await mutateGraphQL({
				mutationData: {
					isBlocked: true,
					User: {
						_id: response.uid,
						firstName: firstName,
						lastName: lastName,
						email: email,
					},
				},
				mutateFunction: createBiltyOps?.mutateFunction,
				mutationInputFunction: createBiltyOps?.mutationInputFunction,
				mutationOutputFunction: createBiltyOps?.mutationOutputFunction,
			})
			return 'success'
		} else {
			return 'failure'
		}*/
	}

	const signInWithEmailAndPassword = async (email, password) => {
		const userCredential = await auth.signInWithEmailAndPassword(email, password)
		if (userCredential) {
			const token = await auth.currentUser?.getIdToken()
			localStorage.setItem('token', JSON.stringify({ token }))
			const processedVariables = GraphQlEnum.User.fetch.normal.getVariables({ _id: userCredential?.user?.uid })
			let userData = await fetchBiltyOps({ variables: processedVariables })
			if (!userData?.data?.biltyOps?.[0]) {
				let queryData = await fetchUserWithEmail({ variables: { where: { _id: userCredential.user.uid } } })
				queryData = queryData?.data?.users?.[0]
				await mutateGraphQL({
					mutationData: {
						isBlocked: true,
						User: {
							_id: queryData?._id,
							firstName: queryData?.firstName,
							lastName: queryData?.lastName,
							email: queryData?.email,
						},
					},
					mutateFunction: createBiltyOps?.mutateFunction,
					mutationInputFunction: createBiltyOps?.mutationInputFunction,
					mutationOutputFunction: createBiltyOps?.mutationOutputFunction,
				})
				userData = await fetchBiltyOps({ variables: processedVariables })
			}
			authenticateUser({ userData: userData?.data?.biltyOps?.[0], storageKey: 'user' })
		}
	}

	const resetPassword = async (email) => {
		await auth.sendPasswordResetEmail(email)
		setLoading(false)
		return 'success'
	}

	const authenticateUser = ({ userData, storageKey }) => {
		const userTypeObject = userData.User?.UserType?.find((value) => ['BiltySuperAdmin', 'BiltyAdmin', 'BaseManager']?.includes(value?.userType))
		const processedData = {
			_id: userData?._id,
			userType: userTypeObject?.userType,
			userTypeId: userTypeObject?._id,
			regions: userData?.Regions,
			profileInfo: {
				_id: userData?.User?._id,
				email: userData?.User?.email,
				firstName: userData?.User?.firstName,
				lastName: userData?.User?.lastName,
			},
		}
		localStorage.setItem(storageKey, JSON.stringify(processedData))
		sessionStorage.setItem(storageKey, JSON.stringify(processedData))
		setUserAtom(processedData)
	}

	const logout = () => {
		if (userAuth) {
			auth.signOut()
			sessionStorage.setItem('user', JSON.stringify(null))
			localStorage.setItem('user', JSON.stringify(null))
			sessionStorage.setItem('token', JSON.stringify(null))
			localStorage.setItem('token', JSON.stringify(null))
			window.location.href = '/'
		}
	}

	const authorization = async ({ signup, forgotPassword, email, password, firstName, lastName }) => {
		setLoading(true)
		try {
			if (forgotPassword) {
				const result = await resetPassword(email)
				if (result === 'success') {
					setAlert({
						type: 'success',
						message: 'Reset link has been sent to your email.',
					})
					setLoading(false)
				}
			} else if (signup) {
				// GCP_DOWNTIME_MITIGATION: disable signup during GCP downtime
				setAlert({
					type: 'error',
					message: 'Registration is temporarily unavailable. Please try again later.',
				})
				setLoading(false)
				/*				const result = await signupUser(email, firstName, lastName)
				if (result === 'success') {
					setAlert({
						type: 'success',
						message: 'You have been registered successfully.',
					})
					setLoading(false)
				} else {
					setAlert({
						type: 'error',
						message: 'The email address is already in use by another account.',
					})
					setLoading(false)
				}*/
			} else {
				await signInWithEmailAndPassword(email, password)
			}
		} catch (error) {
			setLoading(false)
			if (error.code === 'auth/invalid-email' || error.code === 'auth/wrong-password') {
				setAlert({
					type: 'error',
					message: 'Email address or password is incorrect.',
				})
			} else if (error.code === 'auth/user-not-found') {
				setAlert({
					type: 'error',
					message: 'No user record for this email address.',
				})
			} else {
				setAlert({
					type: 'error',
					message: 'Please check your internet connection.',
				})
			}
		}
	}

	return { authorization, logout }
}
